import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { loadPortfolioContext } from "../../services/portfolioApi";
import { fetchPortfolioAllocation } from "../../services/portfolioAllocationApi";
import {
  adaptPortfolioAllocationPie,
  buildFailedMainAllocationPie,
} from "../../services/portfolioAllocationAdapter";
import {
  PARENT_PORTFOLIO_KEYS,
  MAIN_PORTFOLIO_COMPOSITION_TITLE,
  getParentApiId,
  getParentDisplayName,
  getParentMeta,
  getPortfolioMeta,
  isValidParentKey,
  isValidPortfolioId,
  parentHasSubportfolios,
} from "../../config/portfolioConfig";
import { resolveActiveLocale } from "../../services/sharmaReportMessages";

export const fetchPortfolioContext = createAsyncThunk(
  "portfolio/fetchContext",
  async (portfolioId, { rejectWithValue, signal }) => {
    try {
      const data = await loadPortfolioContext(portfolioId, signal);
      return { portfolioId, data };
    } catch (err) {
      if (err?.name === "AbortError") {
        throw err;
      }
      return rejectWithValue(err?.message || "load_failed");
    }
  }
);

export const fetchMainPortfolioAllocation = createAsyncThunk(
  "portfolio/fetchMainAllocation",
  async (_, { getState, rejectWithValue, signal }) => {
    const state = getState();
    const portfolioApiId = selectActivePortfolioApiId(state);
    if (!portfolioApiId) {
      return rejectWithValue("no_portfolio");
    }
    const portfolioDisplayName = resolveCompositionSeriesName(state);
    try {
      const json = await fetchPortfolioAllocation({ portfolioApiId, signal });
      return adaptPortfolioAllocationPie(json, {
        locale: resolveActiveLocale(),
        portfolioDisplayName,
        portfolioApiId,
      });
    } catch (err) {
      if (err?.name === "AbortError") {
        throw err;
      }
      return rejectWithValue(err?.message || "fetch_failed");
    }
  }
);

const initialState = {
  selectedParentKey: null,
  parentLoadStatus: "idle",
  parentLoadError: null,
  selectedPortfolioId: null,
  contextData: null,
  loadStatus: "idle",
  loadError: null,
  latestPendingRequestId: null,
  mainAllocationPie: null,
  mainAllocationPortfolioApiId: null,
  mainAllocationStatus: "idle",
  mainAllocationError: null,
};

function resetSubState(state) {
  state.selectedPortfolioId = null;
  state.contextData = null;
  state.loadStatus = "idle";
  state.loadError = null;
  state.latestPendingRequestId = null;
}

function clearMainAllocationState(state) {
  state.mainAllocationPie = null;
  state.mainAllocationPortfolioApiId = null;
  state.mainAllocationStatus = "idle";
  state.mainAllocationError = null;
}

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {
    clearPortfolio: (state) => {
      state.selectedParentKey = null;
      state.parentLoadStatus = "idle";
      state.parentLoadError = null;
      resetSubState(state);
      clearMainAllocationState(state);
    },
    clearParent: (state) => {
      state.selectedParentKey = null;
      state.parentLoadStatus = "idle";
      state.parentLoadError = null;
      resetSubState(state);
      clearMainAllocationState(state);
    },
    clearSubPortfolio: (state) => {
      resetSubState(state);
    },
    setParent: (state, action) => {
      const parentKey = action.payload;
      if (!isValidParentKey(parentKey)) {
        return;
      }
      state.selectedParentKey = parentKey;
      state.parentLoadError = null;
      state.parentLoadStatus = getParentMeta(parentKey).callsApi
        ? "loading"
        : "succeeded";
      resetSubState(state);
      clearMainAllocationState(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolioContext.pending, (state, action) => {
        state.loadStatus = "loading";
        state.loadError = null;
        state.latestPendingRequestId = action.meta.requestId;
      })
      .addCase(fetchPortfolioContext.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestPendingRequestId) {
          return;
        }
        state.loadStatus = "succeeded";
        state.selectedPortfolioId = action.payload.portfolioId;
        state.contextData = action.payload.data;
        clearMainAllocationState(state);
      })
      .addCase(fetchPortfolioContext.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestPendingRequestId) {
          return;
        }
        if (action.meta.aborted) {
          return;
        }
        state.loadStatus = "failed";
        state.loadError = action.payload || "unknown";
      })
      .addCase(fetchMainPortfolioAllocation.pending, (state) => {
        state.mainAllocationStatus = "loading";
        state.mainAllocationError = null;
      })
      .addCase(fetchMainPortfolioAllocation.fulfilled, (state, action) => {
        state.mainAllocationStatus = "succeeded";
        state.mainAllocationPie = action.payload;
        state.mainAllocationPortfolioApiId =
          action.payload?.portfolioApiId ?? null;
        state.mainAllocationError = null;
      })
      .addCase(fetchMainPortfolioAllocation.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.mainAllocationStatus = "failed";
        state.mainAllocationError = action.payload || "fetch_failed";
        state.mainAllocationPortfolioApiId =
          selectActivePortfolioApiId({ portfolio: state }) ?? null;
        state.mainAllocationPie = buildFailedMainAllocationPie(
          { message: action.payload },
          resolveActiveLocale(),
          resolveCompositionSeriesName({ portfolio: state })
        );
      });
  },
});

export const {
  clearPortfolio,
  clearParent,
  clearSubPortfolio,
  setParent,
} = portfolioSlice.actions;
export default portfolioSlice.reducer;

function resolveActiveSubApiId(p, parentKey) {
  if (!parentHasSubportfolios(parentKey)) return null;
  if (!isValidPortfolioId(p.selectedPortfolioId)) return null;
  if (p.loadStatus !== "succeeded") return null;
  return getPortfolioMeta(p.selectedPortfolioId)?.apiId ?? null;
}

export function selectActivePortfolioApiId(state) {
  const p = state?.portfolio;
  if (!p) return null;
  const parentKey = p.selectedParentKey;
  if (!isValidParentKey(parentKey)) return null;
  if (p.parentLoadStatus !== "succeeded") return null;
  const subApiId = resolveActiveSubApiId(p, parentKey);
  if (subApiId) return subApiId;
  return getParentApiId(parentKey);
}

export function selectActivePortfolioDisplayName(state) {
  const p = state?.portfolio;
  if (!p) return null;
  const parentKey = p.selectedParentKey;
  if (!isValidParentKey(parentKey)) return null;
  if (p.parentLoadStatus !== "succeeded") return null;
  if (
    parentHasSubportfolios(parentKey) &&
    isValidPortfolioId(p.selectedPortfolioId) &&
    p.loadStatus === "succeeded"
  ) {
    return getPortfolioMeta(p.selectedPortfolioId)?.displayName ?? null;
  }
  return getParentDisplayName(parentKey);
}

export function selectPortfolioToolsReady(state) {
  return selectActivePortfolioApiId(state) != null;
}

export function selectCanSelectSubPortfolio(state) {
  return state?.portfolio?.selectedParentKey === PARENT_PORTFOLIO_KEYS.MAIN;
}

export function selectIsMainParentWithoutSub(state) {
  const p = state?.portfolio;
  if (!p) return false;
  return (
    p.selectedParentKey === PARENT_PORTFOLIO_KEYS.MAIN &&
    !isValidPortfolioId(p.selectedPortfolioId)
  );
}

export function selectIsMainParentActive(state) {
  return state?.portfolio?.selectedParentKey === PARENT_PORTFOLIO_KEYS.MAIN;
}

export function selectMainAllocationPie(state) {
  return state?.portfolio?.mainAllocationPie ?? null;
}

export function resolveCompositionSeriesName(state) {
  const p = state?.portfolio;
  if (!p) {
    return MAIN_PORTFOLIO_COMPOSITION_TITLE;
  }
  if (
    p.selectedParentKey === PARENT_PORTFOLIO_KEYS.MAIN &&
    isValidPortfolioId(p.selectedPortfolioId) &&
    p.loadStatus === "succeeded"
  ) {
    return (
      getPortfolioMeta(p.selectedPortfolioId)?.displayName ??
      MAIN_PORTFOLIO_COMPOSITION_TITLE
    );
  }
  return MAIN_PORTFOLIO_COMPOSITION_TITLE;
}
